import { Stack, Typography } from '@mui/material';
import type {
  BackupVault,
  InventoryResource,
  Snapshot,
} from '@repo/api-gw-sdk';

import { Panels } from '@/components/panels/panels';
import { useViewParameters } from '@/contexts/navigation';
import { useWorkspace } from '@/contexts/useWorkspace';

import type { MutateSnapshots } from './eonSnapshotsTab';
import { FsxSnapshotDetails } from './fsxSnapshotDetails';

export const FsxSnapshots = ({
  snapshots,
  entity,
  selectedSnapshot,
  setSelectedSnapshot,
  vaults,
  mutateSnapshots,
}: {
  snapshots: Snapshot[];
  entity: InventoryResource;
  selectedSnapshot: string;
  setSelectedSnapshot: (snapshotId: string) => void;
  vaults: BackupVault[];
  mutateSnapshots: MutateSnapshots;
}) => {
  const { rightPanel } = useWorkspace();
  const { removeParam } = useViewParameters();

  if (snapshots.length === 0) {
    return <FsxSnapshotsEmptyState />;
  }

  const onClose = () => {
    rightPanel.setIsOpen(false);
    removeParam('id');
  };

  const onBack = () => {
    rightPanel.setComponent({
      panel: Panels.InventoryInstance,
      props: {
        id: entity.id,
        onClose,
        initialTab: 'eon-snapshots',
      },
    });
  };

  return (
    <Stack width='100%' overflow='auto'>
      {snapshots.map((snap) => {
        const vault = vaults.find((v) => v.id === snap.vaultId);
        return (
          <FsxSnapshotDetails
            resource={entity}
            key={snap.id}
            isSelected={selectedSnapshot === snap.id}
            snap={snap}
            vault={vault}
            onSelect={() =>
              setSelectedSnapshot(selectedSnapshot === snap.id ? '' : snap.id)
            }
            snapshotSelection={snapshots.length > 1}
            mutateSnapshots={mutateSnapshots}
            onRestore={(selectedVolume) => {
              // Find the index of the selected volume for ONTAP restore
              const backedUpVolumes =
                snap.fsxSnapshotProperties?.backedUpVolumes || [];
              const selectedVolumeIndex = selectedVolume
                ? backedUpVolumes.findIndex(
                    (v) => v.volumeId === selectedVolume.volumeId
                  )
                : undefined;

              rightPanel.setComponent({
                panel: Panels.RestoreFSxWizard,
                props: {
                  snapshot: snap,
                  resource: entity,
                  onClose,
                  onBack,
                  selectedVolumeIndex:
                    selectedVolumeIndex !== undefined &&
                    selectedVolumeIndex !== -1
                      ? selectedVolumeIndex
                      : undefined,
                },
              });
            }}
          />
        );
      })}
    </Stack>
  );
};

const FsxSnapshotsEmptyState = () => {
  return (
    <Stack height='400px' alignItems='center' justifyContent='center'>
      <Typography variant='body1' width='260px' textAlign='center'>
        {`No FSx snapshots yet. Backup may still be running — snapshots will
      appear once it's complete.`}
      </Typography>
    </Stack>
  );
};
